import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileSpreadsheet, Truck, CheckCircle2, Edit2, Save,
  ChevronDown, ChevronUp, X, FileText, Clock, RotateCcw, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API   = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('authToken');

const LAB_COLORS = {
  GIA: 'bg-emerald-600', IGI: 'bg-blue-500', HRD: 'bg-purple-500', SGL: 'bg-orange-500',
};

const STATUS_META = {
  prepared:        { label: 'Prepared',   color: 'text-sky-700 bg-sky-100' },
  dispatched:      { label: 'Dispatched', color: 'text-amber-700 bg-amber-100' },
  results_entered: { label: 'Results In', color: 'text-emerald-700 bg-emerald-100' },
};

const JEWELLERY_TYPES = ['Ring', 'Earrings', 'Necklace', 'Pendant', 'Bracelet', 'Bangle', 'Chain', 'Brooch', 'Other'];
const METALS          = ['Gold', 'Silver', 'Platinum', 'White Gold', 'Rose Gold'];
const METAL_COLORS    = ['Yellow', 'White', 'Rose', 'N/A'];
const PURITIES        = ['22K', '18K', '14K', '10K', '9K', '925', '950', 'Other'];
const COLORS          = ['D','E','F','G','H','I','J','K','L','M'];
const CLARITIES       = ['IF','VVS1','VVS2','VS1','VS2','SI1','SI2','I1','I2','I3'];

export default function LabBatchDetail() {
  const { batchId } = useParams();
  const navigate    = useNavigate();
  const { currentUser } = useAuth();

  const [batch,        setBatch]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [editMode,     setEditMode]     = useState(false);
  const [challan,      setChallan]      = useState({});
  const [items,        setItems]        = useState([]);
  const [results,      setResults]      = useState([]);
  const [expandedIdx,  setExpandedIdx]  = useState(null);
  const [resultIdx,    setResultIdx]    = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [actioning,    setActioning]    = useState(false);
  const [savingResult, setSavingResult] = useState({});   // { [idx]: bool }
  const [validation,   setValidation]   = useState({ ok: true, errors: [] });

  const role = currentUser?.role || '';
  const canAction = ['admin', 'production_supervisor', 'metal_inventory_assistant'].includes(role);
  const canCreate = ['admin', 'production_supervisor'].includes(role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/lab-batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const b = res.data;
      setBatch(b);
      setChallan(b.challan_details || {});
      let loadedItems = b.items || [];

      // ── ERP auto-prefill: silently fill jewellery_type for items where it's blank ──
      // The backend endpoint never overwrites a manually-entered value.
      const hasBlankJT = loadedItems.some(it => !(it.jewellery_type || '').trim());
      if (hasBlankJT && b.status === 'prepared') {
        try {
          const prefillRes = await axios.post(
            `${API}/api/lab-batches/${batchId}/erp-prefill`, {},
            { headers: { Authorization: `Bearer ${token()}` } }
          );
          if (prefillRes.data?.items_filled > 0) {
            // Reload to pick up the freshly-written items
            const reload = await axios.get(`${API}/api/lab-batches/${batchId}`, {
              headers: { Authorization: `Bearer ${token()}` },
            });
            loadedItems = reload.data.items || [];
            toast.success(`Auto-filled Jewelry Type for ${prefillRes.data.items_filled} item(s) from ERP`);
          }
        } catch (e) {
          // Silent — prefill is best-effort
        }
      }

      setItems(loadedItems);
      // Pre-populate result_cts with original DV tag weight if pending — keep editable
      const itemByTagId = Object.fromEntries(loadedItems.map(it => [it.tag_id, it]));
      const seededResults = (b.lab_results || []).map(r => {
        if (r.status === 'pending' && (r.result_cts === null || r.result_cts === undefined || r.result_cts === 0 || r.result_cts === '')) {
          const matching = itemByTagId[r.tag_id];
          if (matching && matching.diamond_total_ct_wt != null) {
            return { ...r, result_cts: matching.diamond_total_ct_wt };
          }
        }
        return r;
      });
      setResults(seededResults);
      // Pull validation status (mandatory fields)
      try {
        const v = await axios.get(`${API}/api/lab-batches/${batchId}/validate`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setValidation(v.data);
      } catch {
        setValidation({ ok: true, errors: [] });
      }
    } catch (err) {
      toast.error('Failed to load batch');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/lab-batches/${batchId}`, {
        challan_details: challan,
        items,
      }, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Batch updated');
      setEditMode(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (endpoint, successMsg) => {
    setActioning(true);
    try {
      await axios.patch(`${API}/api/lab-batches/${batchId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setActioning(false);
    }
  };

  const saveTagResult = async (idx) => {
    const r = results[idx];
    if (!r.status || r.status === 'pending') {
      toast.error('Please mark as Entered or Rejected first');
      return;
    }
    setSavingResult(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await axios.patch(
        `${API}/api/lab-batches/${batchId}/results/${r.tag_id}`,
        {
          cert_number:      r.cert_number,
          result_colors:    r.result_colors,
          result_clarities: r.result_clarities,
          result_cts:       r.result_cts,
          notes:            r.notes,
          status:           r.status,
        },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const pending = res.data.pending_count;
      toast.success(pending === 0
        ? 'All results in — batch closed!'
        : `Saved! ${pending} tag${pending !== 1 ? 's' : ''} still pending`);
      setResultIdx(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingResult(prev => ({ ...prev, [idx]: false }));
    }
  };

  const downloadExcel = () => {
    axios.get(`${API}/api/lab-batches/${batchId}/excel`, {
      headers: { Authorization: `Bearer ${token()}` },
      responseType: 'blob',
    }).then(res => {
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href    = url;
      const dt  = batch?.dispatched_at || batch?.created_at;
      const dateStr = dt ? new Date(dt).toLocaleDateString('en-GB').replace(/\//g, '-') : 'draft';
      a.download = `Mounted_Packing_List_${dateStr}_${batch?.batch_number || batchId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error('Excel generation failed'));
  };

  const updateItem = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };

  const updateResult = (idx, key, val) => {
    setResults(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };

  const toggleResultColor = (idx, color) => {
    const r = results[idx];
    const cur = r.result_colors || [];
    const next = cur.includes(color) ? cur.filter(c => c !== color) : [...cur, color];
    updateResult(idx, 'result_colors', next);
  };

  const toggleResultClarity = (idx, clarity) => {
    const r = results[idx];
    const cur = r.result_clarities || [];
    const next = cur.includes(clarity) ? cur.filter(c => c !== clarity) : [...cur, clarity];
    updateResult(idx, 'result_clarities', next);
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 max-w-md mx-auto items-center justify-center">
        <RotateCcw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!batch) return null;

  const sm = STATUS_META[batch.status] || STATUS_META.prepared;

  return (
    <div className="flex flex-col h-screen bg-gray-50 max-w-md mx-auto">
      {/* Header */}
      <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-emerald-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full text-white font-bold ${LAB_COLORS[batch.lab] || 'bg-gray-500'}`}>
              {batch.lab}
            </span>
            <span className="text-base font-bold font-mono">{batch.batch_number}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${sm.color}`}>{sm.label}</span>
            <span className="text-[10px] text-emerald-200">→ {batch.assigned_to_name}</span>
          </div>
        </div>
        {batch.status === 'prepared' && canAction && (
          <button
            onClick={() => setEditMode(v => !v)}
            className={`p-2 rounded-full ${editMode ? 'bg-white/30' : 'hover:bg-emerald-600'}`}
          >
            {editMode ? <X className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />}
          </button>
        )}
        <button
          onClick={downloadExcel}
          className="p-2 rounded-full hover:bg-emerald-600"
          data-testid="download-excel-btn"
          title="Download Excel"
        >
          <FileSpreadsheet className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Validation banner */}
        {batch.status === 'prepared' && !validation.ok && (
          <div className="mx-4 mt-4 mb-2 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3" data-testid="validation-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-amber-800">Cannot dispatch yet — fill the missing fields</p>
                <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                  {validation.errors.slice(0, 30).map((err, i) => (
                    <li key={i} className="text-[11px] text-amber-700">• {err}</li>
                  ))}
                  {validation.errors.length > 30 && (
                    <li className="text-[11px] text-amber-600 italic">+ {validation.errors.length - 30} more…</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Challan Details */}
        <div className="mx-4 mt-4 mb-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Challan Details</p>
            <FileText className="w-4 h-4 text-gray-300" />
          </div>
          <div className="px-4 py-3 space-y-3">
            {[
              { key: 'memo_no',           label: 'Memo No.',         placeholder: 'e.g. M-2026-0142' },
              { key: 'report_service',    label: 'Report Service',   placeholder: 'MJG+JGC' },
              { key: 'notes',             label: 'Notes',            placeholder: 'Any special instructions' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <p className="text-[10px] font-semibold text-gray-400 uppercase mb-0.5">{label}</p>
                {editMode ? (
                  <input
                    value={challan[key] || ''}
                    onChange={e => setChallan(c => ({ ...c, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400"
                    data-testid={`challan-${key}`}
                  />
                ) : (
                  <p className="text-sm text-gray-800">{challan[key] || <span className="text-gray-300 italic">—</span>}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Items */}
        <div className="mx-4 mb-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Items · {items.length} {items.length === 1 ? 'tag' : 'tags'}
            </p>
          </div>
          <div className="divide-y divide-gray-50">
            {items.map((item, idx) => (
              <div key={item.tag_id}>
                <button
                  onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left"
                  data-testid={`item-toggle-${item.tag_number}`}
                >
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 font-mono">#{item.tag_number}</p>
                    {item.jewellery_ref && <p className="text-xs text-gray-500 truncate">{item.jewellery_ref}</p>}
                    <div className="flex gap-2 mt-0.5 flex-wrap">
                      {item.diamond_total_ct_wt != null && (
                        <span className="text-[10px] text-emerald-700 font-bold">{parseFloat(item.diamond_total_ct_wt).toFixed(3)} cts</span>
                      )}
                      {item.clarity && <span className="text-[10px] text-gray-500">{item.clarity}</span>}
                      {item.color_range && <span className="text-[10px] text-gray-500">{item.color_range}</span>}
                    </div>
                  </div>
                  {expandedIdx === idx ? <ChevronUp className="w-4 h-4 text-gray-300" /> : <ChevronDown className="w-4 h-4 text-gray-300" />}
                </button>

                {expandedIdx === idx && editMode && (
                  <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                    {[
                      { key: 'jewellery_type',    label: 'Jewelry Type',    type: 'select', options: JEWELLERY_TYPES },
                      { key: 'metal',             label: 'Metal',           type: 'select', options: METALS },
                      { key: 'metal_color',       label: 'Metal Color',     type: 'select', options: METAL_COLORS },
                      { key: 'metal_purity',      label: 'Purity',          type: 'select', options: PURITIES },
                      { key: 'estimated_value_usd', label: 'Est. Value ($)', type: 'number' },
                      { key: 'item_weight_g',     label: 'Item Wt (g)',     type: 'number' },
                      { key: 'diamond_qty',       label: 'Diamond Qty (pcs)', type: 'number' },
                      { key: 'diamond_total_ct_wt', label: 'Total Ct Wt',   type: 'number' },
                      { key: 'jewellery_size',    label: 'Jewelry Size',    type: 'text' },
                      { key: 'client_marking',    label: 'Client Marking',  type: 'text' },
                    ].map(({ key, label, type, options }) => (
                      <div key={key}>
                        <p className="text-[9px] font-semibold text-gray-400 uppercase mb-0.5">{label}</p>
                        {type === 'select' ? (
                          <select
                            value={item[key] || ''}
                            onChange={e => updateItem(idx, key, e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-emerald-400"
                          >
                            <option value="">—</option>
                            {options.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input
                            type={type}
                            step={type === 'number' ? (key === 'diamond_qty' ? '1' : '0.001') : undefined}
                            value={item[key] ?? ''}
                            onChange={e => {
                              const v = e.target.value;
                              if (type === 'number') {
                                updateItem(idx, key, v === '' ? null : (key === 'diamond_qty' ? parseInt(v, 10) || null : parseFloat(v)));
                              } else {
                                updateItem(idx, key, v);
                              }
                            }}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-emerald-400"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {expandedIdx === idx && !editMode && (
                  <div className="px-4 pb-4 grid grid-cols-2 gap-x-4 gap-y-1">
                    {[
                      ['Jewelry Type',  item.jewellery_type],
                      ['Metal',         item.metal],
                      ['Metal Color',   item.metal_color],
                      ['Purity',        item.metal_purity],
                      ['Est. Value',    item.estimated_value_usd != null ? `$${item.estimated_value_usd}` : null],
                      ['Item Wt',       item.item_weight_g != null ? `${item.item_weight_g}g` : null],
                      ['Diamond Qty',   item.diamond_qty != null ? `${item.diamond_qty} pcs` : null],
                      ['Total Ct Wt',   item.diamond_total_ct_wt != null ? `${parseFloat(item.diamond_total_ct_wt).toFixed(3)} ct` : null],
                      ['Jewelry Size',  item.jewellery_size],
                      ['Client Marking',item.client_marking],
                    ].map(([lbl, val]) => (
                      <div key={lbl}>
                        <p className="text-[9px] font-semibold text-gray-400 uppercase">{lbl}</p>
                        <p className="text-xs text-gray-700">{val || <span className="text-gray-300">—</span>}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Lab Results — shown once dispatched (instalment returns) */}
        {['dispatched', 'results_entered'].includes(batch.status) && (
          <div className="mx-4 mb-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Lab Results</p>
                {(() => {
                  const pending  = results.filter(r => r.status === 'pending').length;
                  const returned = results.filter(r => r.status !== 'pending').length;
                  return pending > 0 ? (
                    <p className="text-[10px] text-amber-600 font-semibold mt-0.5">
                      {returned} of {results.length} returned · <span className="text-red-500">{pending} pending</span>
                    </p>
                  ) : (
                    <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">All {results.length} tags returned</p>
                  );
                })()}
              </div>
              <CheckCircle2 className="w-4 h-4 text-gray-300" />
            </div>
            <div className="divide-y divide-gray-50">
              {results.map((r, idx) => (
                <div key={r.tag_id}>
                  <button
                    onClick={() => setResultIdx(resultIdx === idx ? null : idx)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left"
                    data-testid={`result-toggle-${r.tag_number}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                      r.status === 'entered'  ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'rejected' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {r.status === 'entered' ? '✓' : r.status === 'rejected' ? '✗' : idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 font-mono">#{r.tag_number}</p>
                      {r.cert_number && <p className="text-xs text-gray-500">Cert: {r.cert_number}</p>}
                      {r.returned_at && (
                        <p className="text-[10px] text-gray-400">
                          {new Date(r.returned_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </p>
                      )}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      r.status === 'entered'  ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'rejected' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {r.status === 'entered' ? 'Entered' : r.status === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                    {resultIdx === idx ? <ChevronUp className="w-4 h-4 text-gray-300" /> : <ChevronDown className="w-4 h-4 text-gray-300" />}
                  </button>

                  {resultIdx === idx && canAction && (
                    <div className="px-4 pb-4 space-y-3 bg-gray-50/50">
                      <div>
                        <p className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Cert Number</p>
                        <input
                          value={r.cert_number || ''}
                          onChange={e => updateResult(idx, 'cert_number', e.target.value)}
                          placeholder="e.g. 7472831052"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 bg-white"
                          data-testid={`result-cert-${r.tag_number}`}
                        />
                      </div>

                      <div>
                        <p className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Result Colours</p>
                        <div className="flex flex-wrap gap-1.5">
                          {COLORS.map(c => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => toggleResultColor(idx, c)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                (r.result_colors || []).includes(c)
                                  ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
                              }`}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Result Clarity</p>
                        <div className="flex flex-wrap gap-1.5">
                          {CLARITIES.map(c => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => toggleResultClarity(idx, c)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                (r.result_clarities || []).includes(c)
                                  ? 'bg-sky-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
                              }`}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Result Ct Wt</p>
                          <input
                            type="number"
                            step="0.001"
                            value={r.result_cts ?? ''}
                            onChange={e => {
                              const v = e.target.value;
                              updateResult(idx, 'result_cts', v === '' ? null : parseFloat(v));
                            }}
                            placeholder="0.000"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 bg-white"
                            data-testid={`result-cts-${r.tag_number}`}
                          />
                          <p className="text-[9px] text-gray-400 mt-0.5">Pre-filled from dispatch — edit if lab differs</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Notes</p>
                          <input
                            value={r.notes || ''}
                            onChange={e => updateResult(idx, 'notes', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 bg-white"
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => updateResult(idx, 'status', 'entered')}
                          className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                            r.status === 'entered' ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-emerald-50'
                          }`}
                          data-testid={`mark-entered-${r.tag_number}`}
                        >
                          Mark Entered
                        </button>
                        <button
                          onClick={() => updateResult(idx, 'status', 'rejected')}
                          className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                            r.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-red-50'
                          }`}
                          data-testid={`mark-rejected-${r.tag_number}`}
                        >
                          Mark Rejected
                        </button>
                      </div>

                      <button
                        onClick={() => saveTagResult(idx)}
                        disabled={savingResult[idx]}
                        className="w-full py-3 bg-emerald-600 text-white font-bold rounded-2xl disabled:opacity-50 active:bg-emerald-700 flex items-center justify-center gap-2"
                        data-testid={`save-result-${r.tag_number}`}
                      >
                        <Save className="w-4 h-4" />
                        {savingResult[idx] ? 'Saving…' : 'Save This Result'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="mx-4 mb-4 bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Timeline</p>
          <div className="space-y-1.5">
            {[
              ['Created',    batch.created_at,          batch.created_by_name],
              ['Dispatched', batch.dispatched_at,        null],
              ['Returned',   batch.returned_at,          null],
              ['Results In', batch.results_entered_at,   null],
            ].map(([lbl, ts, by]) => ts && (
              <div key={lbl} className="flex items-center gap-2 text-xs">
                <span className="w-20 shrink-0 text-gray-400 font-medium">{lbl}</span>
                <span className="text-gray-700">{new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                {by && <span className="text-gray-400">by {by}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      {canAction && (
        <div className="bg-white border-t border-gray-100 px-4 py-3 flex gap-2 shrink-0">
          {editMode ? (
            <button
              onClick={saveEdits}
              disabled={saving}
              className="flex-1 py-3 bg-emerald-600 text-white font-bold rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-edits-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Edits'}
            </button>
          ) : batch.status === 'prepared' ? (
            <button
              onClick={() => doAction('dispatch', 'Marked as dispatched!')}
              disabled={actioning || !validation.ok}
              className="flex-1 py-3 bg-amber-500 text-white font-bold rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:bg-amber-600"
              data-testid="dispatch-btn"
              title={!validation.ok ? `Fill missing fields first (${validation.errors.length})` : ''}
            >
              <Truck className="w-4 h-4" />
              {actioning ? 'Processing…' : !validation.ok ? `Fill ${validation.errors.length} field${validation.errors.length === 1 ? '' : 's'}` : 'Mark as Dispatched'}
            </button>
          ) : batch.status === 'dispatched' ? (
            <div className="flex-1 py-3 bg-amber-50 text-amber-700 font-bold rounded-2xl flex items-center justify-center gap-2 text-sm">
              <Clock className="w-4 h-4" />
              Awaiting returns — enter results above as tags arrive
            </div>
          ) : batch.status === 'results_entered' ? (
            <div className="flex-1 py-3 bg-emerald-50 text-emerald-700 font-bold rounded-2xl flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              All Results Entered — Batch Closed
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
